import { useEffect, useState, useCallback, useRef } from 'preact/hooks'
import type { ChatSession, Persona } from '../types'

interface SidebarProps {
  userEmail: string
  onSignOut: () => void
  personas: Persona[]
  selectedPersona: Persona
  onSelectPersona: (persona: Persona) => void
  onNewChat: () => void
  onStopChat: () => void
  isSessionActive: boolean
  sessions: ChatSession[]
  sessionsLoading: boolean
  sessionsLoadingMore: boolean
  hasMoreSessions: boolean
  onSelectSession: (session: ChatSession) => void
  onDeleteSession: (sessionId: string) => void
  onRenameSession: (sessionId: string, name: string) => void
  onRefreshSessions: (persona?: string) => void
  onLoadMoreSessions: () => void
  onGoHome?: () => void
  personaImages: Record<string, string>
  onClose?: () => void
  onNavigate?: (view: string) => void
}

const DATA_TILES: { view: string; label: string; image: string; fallback: string }[] = [
  { view: 'tasks',    label: 'Tasks',    image: 'toon_sea_smol_tasks.png',     fallback: '\u{2705}' },
  { view: 'notes',    label: 'Notes',    image: 'toon_sea_smol_notes.png',     fallback: '\u{1F4DD}' },
  { view: 'calls',    label: 'Calls',    image: 'toon_sea_smol_followups.png', fallback: '\u{1F4DE}' },
  { view: 'email',    label: 'Email',    image: 'toon_sea_smol_email.png',     fallback: '\u{1F4E7}' },
  { view: 'files',    label: 'Files',    image: 'toon_sea_smol_files.png',     fallback: '\u{1F4C1}' },
  { view: 'calendar', label: 'Calendar', image: 'toon_sea_smol_calendar.png',  fallback: '\u{1F4C5}' },
  { view: 'box',      label: 'BOX',      image: 'toon_sea_smol_box.png',       fallback: '\u{1F4E6}' },
  { view: 'apps',     label: 'APPS',     image: 'toon_sea_smol_apps.png',      fallback: '\u{1F9E9}' },
]

function DataShortcuts({ onNavigate }: { onNavigate: (view: string) => void }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4,
      width: '100%', padding: '10px 0 4px', borderTop: '1px solid var(--border)',
    }}>
      {DATA_TILES.map((tile) => (
        <button
          key={tile.view}
          onClick={() => onNavigate(tile.view)}
          title={tile.label}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 2, padding: '6px 2px', background: 'transparent',
            border: 'none', borderRadius: 6, cursor: 'pointer', color: 'var(--text-secondary)',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(45,165,160,0.08)' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
        >
          <img
            src={`/buttons/${tile.image}`}
            alt={tile.label}
            style={{ width: 36, height: 36, objectFit: 'contain' }}
            onError={(e) => {
              const el = e.target as HTMLImageElement
              el.style.display = 'none'
              el.parentElement!.querySelector('.tile-fb')?.removeAttribute('hidden')
            }}
          />
          <span class="tile-fb" hidden style={{ fontSize: 16 }}>{tile.fallback}</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{tile.label}</span>
        </button>
      ))}
    </div>
  )
}

export function Sidebar({
  userEmail,
  onSignOut,
  personas,
  selectedPersona,
  onSelectPersona,
  onNewChat,
  onStopChat,
  isSessionActive,
  sessions,
  sessionsLoading,
  sessionsLoadingMore,
  hasMoreSessions,
  onSelectSession,
  onDeleteSession,
  onRenameSession,
  onRefreshSessions,
  onLoadMoreSessions,
  onGoHome,
  personaImages,
  onClose,
  onNavigate,
}: SidebarProps) {
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [stopping, setStopping] = useState(false)
  const sessionListRef = useRef<HTMLDivElement | null>(null)

  // Refresh sessions on mount and persona change
  useEffect(() => {
    onRefreshSessions(selectedPersona.id)
  }, [selectedPersona.id, onRefreshSessions])

  // Infinite scroll: when scrolled within 120px of the bottom, load more.
  const handleSessionListScroll = useCallback(() => {
    const el = sessionListRef.current
    if (!el || !hasMoreSessions || sessionsLoadingMore || sessionsLoading) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    if (distanceFromBottom < 120) {
      onLoadMoreSessions()
    }
  }, [hasMoreSessions, sessionsLoadingMore, sessionsLoading, onLoadMoreSessions])

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpenId) return
    const handler = () => setMenuOpenId(null)
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [menuOpenId])

  const handleDelete = useCallback((e: Event, sessionId: string) => {
    e.stopPropagation()
    setMenuOpenId(null)
    if (confirm('Delete this chat session?')) {
      onDeleteSession(sessionId)
    }
  }, [onDeleteSession])

  const handleRenameStart = useCallback((e: Event, session: ChatSession) => {
    e.stopPropagation()
    setMenuOpenId(null)
    setRenamingId(session.id)
    setRenameValue(session.summary || '')
  }, [])

  const handleRenameSubmit = useCallback((sessionId: string) => {
    const trimmed = renameValue.trim()
    if (trimmed) {
      onRenameSession(sessionId, trimmed)
    }
    setRenamingId(null)
    setRenameValue('')
  }, [renameValue, onRenameSession])

  const selectedImage = personaImages[selectedPersona.id]

  return (
    <div class="sidebar">
      {/* Row 1: Home + Close */}
      <div class="sidebar__section" style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 8 }}>
        {onGoHome && (
          <button class="btn btn-sm" onClick={onGoHome} title="Home" style={{ fontSize: 18, padding: '4px 8px', flexShrink: 0 }}>{'\u{1F3E0}'}</button>
        )}
        {onClose && (
          <button class="btn btn-sm" onClick={onClose} title="Close sidebar" style={{ fontSize: 14, padding: '4px 8px', flexShrink: 0 }}>{'\u{25C0}'}</button>
        )}
        <span style={{ flex: 1 }} />
      </div>

      {/* Session controls */}
      <div class="sidebar__section">
        {isSessionActive || stopping ? (
          <button
            class="btn btn-danger btn-block"
            disabled={stopping}
            onClick={() => {
              setStopping(true)
              Promise.resolve(onStopChat()).finally(() => setStopping(false))
            }}
          >
            {stopping ? 'Closing...' : 'Stop Chat'}
          </button>
        ) : (
          <button class="btn btn-primary btn-block" onClick={onNewChat}>
            New Chat
          </button>
        )}
      </div>

      {/* Chat history */}
      <div class="sidebar__section sidebar__history">
        <div class="sidebar__label">
          Sessions
          <button class="btn btn-sm" onClick={() => onRefreshSessions(selectedPersona.id)} disabled={sessionsLoading}>
            {sessionsLoading ? '...' : 'Refresh'}
          </button>
        </div>
        <div
          class="sidebar__session-list"
          ref={sessionListRef}
          onScroll={handleSessionListScroll}
        >
          {sessions.length === 0 && !sessionsLoading && (
            <div class="sidebar__empty">No sessions yet</div>
          )}
          {sessions.map((s) => {
            return (
              <div
                key={s.id}
                class="sidebar__session-row"
                onClick={(e) => {
                  const target = e.target as HTMLElement
                  if (target.closest('.sidebar__session-menu-wrap') || target.closest('.sidebar__rename')) return
                  onSelectSession(s)
                }}
                title={`${s.persona} - ${s.id}`}
              >
                {renamingId === s.id ? (
                  <div class="sidebar__rename" onClick={(e) => e.stopPropagation()}>
                    <input
                      class="sidebar__rename-input"
                      value={renameValue}
                      onInput={(e) => setRenameValue((e.target as HTMLInputElement).value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSubmit(s.id)
                        if (e.key === 'Escape') { setRenamingId(null); setRenameValue('') }
                      }}
                      autoFocus
                    />
                    <button class="btn btn-sm btn-primary" onClick={() => handleRenameSubmit(s.id)}>OK</button>
                    <button class="btn btn-sm" onClick={() => { setRenamingId(null); setRenameValue('') }}>X</button>
                  </div>
                ) : (
                  <>
                    <span class="sidebar__session-info">
                      <span class="sidebar__session-date">
                        {(() => {
                          const ts = s.messages_updated_at || s.ended_at || s.started_at
                          return `${new Date(ts).toLocaleDateString()} ${new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                        })()}
                      </span>
                      <span class="sidebar__session-summary">{s.summary || 'New Chat'}</span>
                    </span>
                    <div class="sidebar__session-menu-wrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        class="sidebar__menu-btn"
                        onClick={() => setMenuOpenId(menuOpenId === s.id ? null : s.id)}
                        title="Actions"
                      >
                        &#x22EE;
                      </button>
                      {menuOpenId === s.id && (
                        <div class="sidebar__menu-dropdown">
                          <button class="sidebar__menu-item" onClick={(e) => handleRenameStart(e, s)}>
                            Rename
                          </button>
                          <button class="sidebar__menu-item sidebar__menu-item--danger" onClick={(e) => handleDelete(e, s.id)}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
          {sessionsLoadingMore && (
            <div class="sidebar__empty" style={{ padding: '8px' }}>Loading more...</div>
          )}
          {!hasMoreSessions && sessions.length > 0 && (
            <div class="sidebar__empty" style={{ padding: '8px', fontSize: 11 }}>End of history</div>
          )}
        </div>
      </div>

      {/* Data shortcuts — tasks, notes, calls, email, files, calendar */}
      {onNavigate && <DataShortcuts onNavigate={onNavigate} />}

    </div>
  )
}

